/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useState } from 'react';

import { IconButton, InlineNotification, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { LocationIcon } from '@coursera/cds-icons';

import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ProfileViewerCard from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileViewerCard';
import EditProfileDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/EditProfileDialog';
import EditProfilePhotoDialog from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/EditProfilePhotoDialog';
import EmailSection from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/EmailSection';
import PrivacySettingsLink from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/PrivacySettingsLink';
import ProfileButton from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ProfileButton';
import ProfileImage from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ProfileImage';
import ProfileSkeletonLoader from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/ProfileSkeletonLoader';
import { getLocationLabel } from 'bundles/account-profile/components/recruiting-profile-viewer/utils/profileUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/account-profile';

const styles = {
  wrapper: () => css`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    ${breakpoints.down('xs')} {
      align-items: unset;
      justify-content: unset;
      text-align: unset;
      padding: var(--cds-spacing-300);
    }
  `,
  editButtonWrapper: css`
    position: absolute;
    top: 5px;
    right: 5px;
  `,
  fullName: () => css`
    margin-bottom: var(--cds-spacing-100);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    ${breakpoints.down('xs')} {
      /* calculation based on wrapper.padding in XS; 3x is based on 2x not having the desired style and 4x leaving too much white space */
      max-width: calc(100vw - (var(--cds-spacing-300) * 3));
    }
  `,
  fullNameTooltip: () => css`
    max-width: 100%;
    overflow: hidden;
  `,
  sectionTitle: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  location: () => css`
    color: var(--cds-color-grey-600);
    display: flex;
    align-items: flex-start;
    margin-bottom: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-50);
    }
  `,

  locationIcon: () => css`
    margin-right: var(--cds-spacing-50);
    margin-top: var(--cds-spacing-50);
  `,

  pronoun: () => css`
    margin-bottom: var(--cds-spacing-50);
  `,
};

export const PROFILE_SECTION_QUERY_SELECTOR = 'profile-photo-and-invitation-section';

const E2E_SELECTORS = {
  PROFILE_SECTION: 'profile-photo-and-invitation-section',
  PROFILE_SECTION_EDIT: 'profile_edit_intro_profile',
} as const;

const PhotoAndInvitationSection = () => {
  const { profileState, isProfileLoading, editable } = useContext(ProfileContext);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showEditPhotoDialog, setShowEditPhotoDialog] = useState(false);

  return (
    <div id={PROFILE_SECTION_QUERY_SELECTOR}>
      <ProfileViewerCard customCss={styles.wrapper} data-e2e={E2E_SELECTORS.PROFILE_SECTION}>
        <Typography variant="h2semibold" data-testid="photo-section-title" css={styles.sectionTitle}>
          {_t('Personal details')}
        </Typography>

        {editable && (
          <>
            <div css={styles.editButtonWrapper}>
              <IconButton
                size="small"
                onClick={() => setShowEditDialog(true)}
                intent="edit"
                aria-label={_t('Edit personal details.')}
                variant="ghost"
                component={TrackedButton}
                data-e2e={E2E_SELECTORS.PROFILE_SECTION_EDIT}
                // not tracked in eventing v3
                trackingName="profile_edit_intro_profile"
                requireFullyVisible={false}
                withVisibilityTracking={true}
              />
            </div>
            <EditProfileDialog
              isOpen={showEditDialog}
              onClose={() => setShowEditDialog(false)}
              key={`edit-profile-${profileState?.profilePhoto?.default}`}
            />
            <EditProfilePhotoDialog
              isOpen={showEditPhotoDialog}
              onClose={() => setShowEditPhotoDialog(false)}
              key={`edit-profile-photo-${profileState?.profilePhoto?.default}`}
            />
          </>
        )}

        {isProfileLoading ? (
          <ProfileSkeletonLoader />
        ) : (
          <>
            <ProfileImage
              onClick={editable ? () => setShowEditPhotoDialog(true) : undefined}
              // @ts-expect-error URL: string
              image={profileState?.profilePhoto?.default}
              fullName={profileState?.fullName || ''}
            />

            <TooltipWrapper message={profileState?.fullName} placement="top" css={styles.fullNameTooltip}>
              <Typography
                className="pii-hide fs-exclude"
                variant="h1semibold"
                component="p"
                data-testid="profile-photo-invite-section-title"
                css={styles.fullName}
              >
                {profileState?.fullName}
              </Typography>
            </TooltipWrapper>

            {profileState?.pronoun && (
              <Typography2 className="pii-hide fs-exclude" css={styles.pronoun} variant="subtitleMedium" component="p">
                {profileState?.pronoun}
              </Typography2>
            )}
            {profileState?.externalId && <EmailSection externalId={profileState?.externalId} />}
            {(profileState?.location?.cityName ||
              profileState?.location?.stateName ||
              profileState?.location?.countryName) && (
              <div css={styles.location}>
                <LocationIcon css={styles.locationIcon} size="small" title={_t('Location')} role="img" />
                <Typography2 component="p" className="pii-hide fs-exclude" variant="bodyPrimary" color="inherit">
                  {_t('Lives in #{location}', {
                    location: getLocationLabel(profileState?.location),
                  })}
                </Typography2>
              </div>
            )}
            <ProfileButton />
          </>
        )}
        <PrivacySettingsLink />
      </ProfileViewerCard>
    </div>
  );
};

const ErrorBoundPhotoAndInvitationSection = () => {
  return (
    <ErrorBoundaryWithTagging
      componentName="PhotoAndInvitationSection"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the photo and invitation section. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <PhotoAndInvitationSection />
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundPhotoAndInvitationSection;
